import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { InlineSelect } from './StatusBadge';
import { getSalesContingencies, createSalesContingency, updateSalesContingency, deleteSalesContingency, getTeamMembers, getSalesContingenciesRiskReport } from '../lib/api';
import { Plus, Trash, HouseLine, PencilSimple, CheckCircle, Warning, CalendarBlank, CaretDown, CaretUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { StatusChip, TONE } from './enterprise/ui';

const RISK_TONE = { HIGH: 'critical', MODERATE: 'high', LOW: 'good' };

const SALE_STATUSES = ['Active', 'Under Contract', 'Pending', 'Sold', 'Expired', 'Withdrawn'];

const SALE_STATUS_COLORS = {
  'Active': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  'Under Contract': 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  'Pending': 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  'Sold': 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  'Expired': 'bg-red-500/20 text-red-300 border-red-500/40',
  'Withdrawn': 'bg-stone-500/20 text-stone-300 border-stone-500/40',
};

const emptyForm = {
  borrower: '', property_address: '', listed_date: '', listing_price: '',
  under_contract: false, contract_date: '', buyer_agent: '', listing_agent: '',
  expected_sale_date: '', sale_status: 'Active', impact_on_purchase: '', notes: '', assigned_to: ''
};

function ExpandableText({ text, maxLen = 25 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text || text.length <= maxLen) return <span className="text-sm text-muted-foreground">{text || '-'}</span>;
  return (
    <div>
      <span className="text-sm text-muted-foreground">{expanded ? text : text.slice(0, maxLen) + '...'}</span>
      <button onClick={() => setExpanded(p => !p)}
        className="flex items-center gap-0.5 text-[10px] text-primary hover:text-primary/80 font-medium mt-0.5 transition-colors">
        {expanded ? <><CaretUp size={10} /> Less</> : <><CaretDown size={10} /> More</>}
      </button>
    </div>
  );
}

export default function SalesContingenciesTracker() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterStatus, setFilterStatus] = useState('all');
  const [risksByBorrower, setRisksByBorrower] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [d, m, risks] = await Promise.all([getSalesContingencies(), getTeamMembers(), getSalesContingenciesRiskReport().catch(() => [])]);
      setItems(d); setMembers(m);
      const map = {};
      (risks || []).forEach(r => { if (r.borrower) map[r.id] = r.risk; });
      setRisksByBorrower(map);
    } catch (e) { logError('Load error:', e); toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); const iv = setInterval(loadData, 5000); return () => clearInterval(iv); }, [loadData]);

  const assignOptions = useMemo(() => ['Unassigned', ...members.map(m => m.initials)], [members]);

  const handleInlineUpdate = async (id, field, value) => {
    try {
      await updateSalesContingency(id, { [field]: value });
      setItems(p => p.map(i => i.id === id ? { ...i, [field]: value } : i));
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleSave = async () => {
    if (!form.borrower.trim()) { toast.error('Borrower name required'); return; }
    try {
      if (editing) { await updateSalesContingency(editing.id, form); toast.success('Updated'); }
      else { await createSalesContingency(form); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); setForm(emptyForm); loadData();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      borrower: item.borrower || '', property_address: item.property_address || '',
      listed_date: item.listed_date || '', listing_price: item.listing_price || '',
      under_contract: item.under_contract || false, contract_date: item.contract_date || '',
      buyer_agent: item.buyer_agent || '', listing_agent: item.listing_agent || '',
      expected_sale_date: item.expected_sale_date || '', sale_status: item.sale_status || 'Active',
      impact_on_purchase: item.impact_on_purchase || '', notes: item.notes || '',
      assigned_to: item.assigned_to || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await deleteSalesContingency(id); toast.success('Deleted'); loadData(); }
    catch (e) { toast.error('Failed'); }
  };

  const getDaysUntilSale = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000); };

  const sorted = useMemo(() => {
    const filtered = filterStatus === 'all' ? items : items.filter(i => i.sale_status === filterStatus);
    return [...filtered].sort((a, b) => {
      const statusOrder = { Active: 0, 'Under Contract': 1, Pending: 2, Sold: 3, Expired: 4, Withdrawn: 5 };
      return (statusOrder[a.sale_status] || 0) - (statusOrder[b.sale_status] || 0);
    });
  }, [items, filterStatus]);

  const stats = {
    total: items.length,
    active: items.filter(i => i.sale_status === 'Active').length,
    underContract: items.filter(i => i.sale_status === 'Under Contract').length,
    pending: items.filter(i => i.sale_status === 'Pending').length,
    sold: items.filter(i => i.sale_status === 'Sold').length,
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="sales-contingencies-view">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, cls: 'gradient-slate' },
          { label: 'Active Listings', value: stats.active, cls: 'gradient-green' },
          { label: 'Under Contract', value: stats.underContract, cls: 'gradient-blue' },
          { label: 'Pending Sale', value: stats.pending, cls: 'gradient-amber' },
          { label: 'Sold', value: stats.sold, cls: 'gradient-purple' },
        ].map(s => (
          <div key={s.label} className={`${s.cls} rounded-xl p-4 text-white`} data-testid={`sc-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-sm opacity-90">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-muted rounded-lg p-1 overflow-x-auto max-w-full -mx-1 px-1" style={{ WebkitOverflowScrolling: 'touch' }}>
          {['all', ...SALE_STATUSES].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              data-testid={`sc-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                filterStatus === s ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setDialogOpen(true); }}
          className="bg-primary text-primary-foreground hover:opacity-90 h-10 text-sm whitespace-nowrap" data-testid="add-sales-contingency-btn">
          <Plus size={16} className="mr-1.5" /> <span className="hidden sm:inline">Add Sale Contingency</span><span className="sm:hidden">Add</span>
        </Button>
      </div>

      {/* Table */}
      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pl-4">Borrower</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Address</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Listed</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Price</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Sale Status</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Under Contract</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Exp. Sale Date</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Assigned</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Impact</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Notes</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pr-4 w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground text-base">No sales contingencies tracked yet</TableCell></TableRow>
                ) : sorted.map(item => {
                  const daysLeft = getDaysUntilSale(item.expected_sale_date);
                  const risk = risksByBorrower[item.id];
                  return (
                    <TableRow key={item.id} className="border-border hover:bg-muted/30 transition-colors" data-testid={`sc-row-${item.id}`}>
                      <TableCell className="font-semibold text-foreground pl-4" title={risk ? `${risk.level} risk: ${risk.reason} — ${risk.next_action}` : ''}>
                        <div className="flex items-center gap-2">
                          <span>{item.borrower}</span>
                          {risk && <StatusChip tone={RISK_TONE[risk.level] || 'neutral'}>{risk.level}</StatusChip>}
                        </div>
                        {risk && risk.level !== 'LOW' && (
                          <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1" data-testid={`sc-risk-reason-${item.id}`}>{risk.reason}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">{item.property_address || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{item.listed_date || '-'}</TableCell>
                      <TableCell className="text-sm text-foreground whitespace-nowrap">{item.listing_price ? `$${item.listing_price}` : '-'}</TableCell>
                      <TableCell>
                        <InlineSelect value={item.sale_status || 'Active'} options={SALE_STATUSES}
                          onChange={(v) => handleInlineUpdate(item.id, 'sale_status', v)}
                          testId={`sc-status-${item.id}`} colorMap={SALE_STATUS_COLORS} />
                      </TableCell>
                      <TableCell className="text-center">
                        <button onClick={() => handleInlineUpdate(item.id, 'under_contract', !item.under_contract)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-semibold transition-colors ${
                            item.under_contract ? 'bg-emerald-500/20 text-emerald-300' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`} data-testid={`sc-contract-${item.id}`}>
                          <CheckCircle size={14} weight={item.under_contract ? 'fill' : 'regular'} />
                          {item.under_contract ? 'Yes' : 'No'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-muted-foreground whitespace-nowrap">{item.expected_sale_date || '-'}</span>
                          {daysLeft !== null && daysLeft <= 14 && (
                            <span className={`text-xs font-bold ${daysLeft <= 0 ? 'text-red-400' : (daysLeft <= 7 ? 'text-amber-400' : 'text-foreground')}`}>
                              {daysLeft <= 0 ? 'PAST DUE' : `${daysLeft}d`}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <InlineSelect value={item.assigned_to || 'Unassigned'} options={assignOptions}
                          onChange={(v) => handleInlineUpdate(item.id, 'assigned_to', v === 'Unassigned' ? '' : v)}
                          testId={`sc-assigned-${item.id}`} />
                      </TableCell>
                      <TableCell className="max-w-[140px]">
                        <ExpandableText text={item.impact_on_purchase} maxLen={25} />
                      </TableCell>
                      <TableCell className="max-w-[140px]">
                        <ExpandableText text={item.notes} maxLen={25} />
                      </TableCell>
                      <TableCell className="pr-4">
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid={`sc-edit-${item.id}`}>
                            <PencilSimple size={15} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="h-8 w-8 text-red-400 hover:text-red-500" data-testid={`sc-delete-${item.id}`}>
                            <Trash size={15} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="sc-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <HouseLine size={22} className="text-primary" /> {editing ? 'Edit' : 'Add'} Sales Contingency
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Borrower Name *</label>
                <Input value={form.borrower} onChange={(e) => setForm(p => ({ ...p, borrower: e.target.value }))} className="border-border h-10" data-testid="sc-input-borrower" /></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Property Address</label>
                <Input value={form.property_address} onChange={(e) => setForm(p => ({ ...p, property_address: e.target.value }))} className="border-border h-10" data-testid="sc-input-address" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Listed Date</label>
                <Input type="date" value={form.listed_date} onChange={(e) => setForm(p => ({ ...p, listed_date: e.target.value }))} className="border-border h-10" data-testid="sc-input-listed" /></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Listing Price</label>
                <Input value={form.listing_price} onChange={(e) => setForm(p => ({ ...p, listing_price: e.target.value }))} placeholder="350000" className="border-border h-10" data-testid="sc-input-price" /></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Sale Status</label>
                <Select value={form.sale_status} onValueChange={(v) => setForm(p => ({ ...p, sale_status: v }))}>
                  <SelectTrigger className="border-border h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{SALE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Under Contract?</label>
                <Select value={form.under_contract ? 'yes' : 'no'} onValueChange={(v) => setForm(p => ({ ...p, under_contract: v === 'yes' }))}>
                  <SelectTrigger className="border-border h-10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Contract Date</label>
                <Input type="date" value={form.contract_date} onChange={(e) => setForm(p => ({ ...p, contract_date: e.target.value }))} className="border-border h-10" /></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Expected Sale Date</label>
                <Input type="date" value={form.expected_sale_date} onChange={(e) => setForm(p => ({ ...p, expected_sale_date: e.target.value }))} className="border-border h-10" data-testid="sc-input-exp-sale" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Buyer's Agent</label>
                <Input value={form.buyer_agent} onChange={(e) => setForm(p => ({ ...p, buyer_agent: e.target.value }))} className="border-border h-10" /></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Listing Agent</label>
                <Input value={form.listing_agent} onChange={(e) => setForm(p => ({ ...p, listing_agent: e.target.value }))} className="border-border h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Assigned To</label>
                <Select value={form.assigned_to || 'none'} onValueChange={(v) => setForm(p => ({ ...p, assigned_to: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="border-border h-10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Impact on Purchase</label>
                <Input value={form.impact_on_purchase} onChange={(e) => setForm(p => ({ ...p, impact_on_purchase: e.target.value }))} placeholder="e.g. Closing delayed until sale completes" className="border-border h-10" data-testid="sc-input-impact" /></div>
            </div>
            <div><label className="text-sm font-medium text-muted-foreground mb-1.5 block">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="border-border min-h-[70px]" data-testid="sc-input-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground" data-testid="sc-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
